import { useEffect, useState } from 'react';
import { metrics } from '@sentry/core';
import { AuthTokens, useAuthStorage, useIsAuthenticated } from '@data';
import { useShareMailboxContext, useToastsContext } from '@utils';
import {
  getIsNAASupported,
  getIsOnPremises,
  getIsSSOSupported,
  getUserEmail,
  logInfo,
} from '@utils-global';
import { isSafari } from '@constants';
import { authenticateWithNAA } from './naa';
import { authenticateWithSSO } from './sso';
import { authenticateWithDialog } from './dialogAPI';
import { AuthMethod, getAuthMethodOverride } from './authMethod';

const isDevOrStaging = ['development', 'staging'].includes(process.env.TARGET_ENV);

const getPreferredAuthMethod = (): AuthMethod => {
  const override = getAuthMethodOverride();
  if (override) {
    return override;
  }

  if (getIsOnPremises()) {
    return AuthMethod.NONE;
  }

  if (getIsNAASupported()) {
    return AuthMethod.NAA_INTERACTIVE;
  }

  // SSO is excluded on Safari: ITP blocks the 3rd-party cookies MSAL needs for silent renewal.
  // https://github.com/AzureAD/microsoft-authentication-library-for-js/wiki/Known-issue-on-Safari
  if (getIsSSOSupported() && !isSafari) {
    return AuthMethod.SSO;
  }

  return AuthMethod.DIALOG;
};

const getInitialAuthMethod = (): AuthMethod => {
  const preferredMethod = getPreferredAuthMethod();

  // If NAA is supported, first look for an existing session silently.
  if (preferredMethod === AuthMethod.NAA_INTERACTIVE) {
    return AuthMethod.NAA_SILENT;
  }

  return preferredMethod;
};

const countSignIn = (method: AuthMethod, outcome: 'success' | 'failure') => {
  metrics.count('auth.sign_in', 1, { attributes: { method, outcome } });
};

export const useAuthentication = () => {
  const { showToast } = useToastsContext();
  const { isSharedMailbox, originalUserEmail } = useShareMailboxContext();
  const { setTokens } = useAuthStorage();
  const isAuthenticated = useIsAuthenticated();
  const [method, setMethod] = useState<AuthMethod>(getInitialAuthMethod);

  const handleSuccessfulSignIn = (tokens: AuthTokens, signInMethod: AuthMethod) => {
    countSignIn(signInMethod, 'success');
    setTokens(tokens, signInMethod);

    // Reset the preferred method for the next sign-in, for example after a logout,
    // when the Graph token expired.
    setMethod(getPreferredAuthMethod());
  };

  const handleSignInResult = (tokens: AuthTokens | null, nextMethodOnFailure: AuthMethod) => {
    if (!tokens) {
      countSignIn(method, 'failure');
      setMethod(nextMethodOnFailure);
      return;
    }

    handleSuccessfulSignIn(tokens, method);
  };

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    const signIn = async () => {
      if (method === AuthMethod.NAA_SILENT) {
        logInfo('NAA is supported and called.');
        if (isDevOrStaging) {
          showToast('NAA');
        }

        const tokens = await authenticateWithNAA({
          isSharedMailbox,
          interactive: false,
          showToast,
        });
        // If there is no existing session, continue to the login screen and
        // attempt interactive NAA.
        handleSignInResult(tokens, AuthMethod.NAA_INTERACTIVE);

        return;
      }

      if (method === AuthMethod.NAA_INTERACTIVE) {
        logInfo('NAA is supported and called.');
        if (isDevOrStaging) {
          showToast('NAA');
        }

        const tokens = await authenticateWithNAA({ isSharedMailbox, interactive: true, showToast });
        // If NAA failed or is canceled, fall back to the manual login button.
        handleSignInResult(tokens, AuthMethod.DIALOG);

        return;
      }

      if (method === AuthMethod.SSO) {
        logInfo('Fallback SSO is supported and called.');
        if (isDevOrStaging) {
          showToast('SSO');
        }

        const tokens = await authenticateWithSSO(showToast);
        // If SSO failed or is canceled, fall back to the manual login button.
        handleSignInResult(tokens, AuthMethod.DIALOG);
      }
    };

    signIn();
  }, [method, isAuthenticated]);

  const login = () => {
    // On a shared mailbox getUserEmail() returns the shared address, not the user's.
    const userEmail = isSharedMailbox ? originalUserEmail : getUserEmail();
    authenticateWithDialog({
      userEmail,
      onTokens: (tokens) => handleSuccessfulSignIn(tokens, AuthMethod.DIALOG),
      showToast,
    });
  };

  return { method, login };
};
